<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">
        <template v-if="scheduling">Schedule your thread</template>
        <template v-else>Post your thread</template>
      </p>
      <button class="delete" :disabled="status === 'posting'" @click="$emit('close')"></button>
    </header>

    <template v-if="scheduling">
      <section class="modal-card-body">
        <b-field label="Type in when you like to post these tweets">
          <b-input ref="dateinput" placeholder="Tomorrow, Friday at 7pm, Saturday morning, etc..." v-model="scheduledForInput"></b-input>
        </b-field>
        <b-field label="Or select exactly the date and time with the picker below">
          <b-datetimepicker v-model="scheduledForPicker" horizontal-time-picker inline></b-datetimepicker>
        </b-field>
      </section>
      <div class="scheduling-info">
        <b-message v-if="scheduleStatus === 'ready'" type="is-success">
          This thread will post at
          <strong class="text-success">{{ scheduledForTime }}</strong>
          on
          <strong class="time-primary">{{ scheduledForDate }}</strong>
        </b-message>
        <b-message v-else-if="scheduleStatus === 'past'" type="is-danger">
          Please enter a date in the future!
        </b-message>
        <b-message v-else type="is-info">
          Type in or pick a date to set a scheduled date and time to publish.
        </b-message>

      </div>

    </template>
    <section v-else class="modal-card-body">
      <div v-if="status==='error'" class="notification is-danger">
        <p>
          Oh no! An error occurred when submitting your thread.
          Things got stuck on
          <span v-if="errorTweetIndex !== undefined"
                class="tag is-danger is-light is-medium">Tweet {{ errorTweetIndex + 1 }}</span>
          - <em>{{ errorMsg }}</em>
        </p>
        <p v-if="errorTweetIndex > 0">
          Note that tweets before this one <strong>have been posted to Twitter. </strong>
        </p>

      </div>

      <div v-else-if="status==='posting'" class="notification is-info">
        <p>Hold on, we're posting your thread...</p>
      </div>

      <div v-else-if="status==='success'" class="notification is-success">
        <p>
          <template v-if="scheduling">Your twitter thread has been scheduled.</template>
          <template v-else>Your twitter thread has been posted.</template>
        </p>
      </div>

      <div v-else class="notification">
        <p>
            <span v-if="$store.getters.tweets.length === 1">
              Posting a
              <b-tag type="is-info">single tweet</b-tag>
            </span>
          <span v-else>
              Posting a twitter thread consisting of
              <b-tag type="is-info">{{ $store.getters.tweets.length }} tweets</b-tag>
            </span>

          <span v-if="$store.getters.totalImagesInActiveThread">
              and <b-tag type="is-info">{{ $store.getters.totalImagesInActiveThread }} images</b-tag>
            </span>

          <span v-if="$store.getters.replyTo">
              in reply to a tweet by
              <link-to-tweet :id="$store.getters.replyTo.id" :screen_name="$store.getters.replyTo.user.screen_name">
                <b-tag class="is-info">@{{ $store.getters.replyTo.user.screen_name }}</b-tag>
              </link-to-tweet>
            </span>
        </p>

      </div>

      <div v-if="status === 'success' && this.postedTweets[0] && this.postedTweets[0].user">
        <tweet :data="this.postedTweets[0]" :show-media="false" :show-line-down="this.postedTweets.length > 1"></tweet>
        <tweet v-if="this.postedTweets.length === 3" :show-line-up="true" :show-line-down="true"
               :data="this.postedTweets[1]" :show-media="false"></tweet>
        <div v-else-if="this.postedTweets.length > 3" class="card tweet-card">
          <div class="card-content">
            <div class="media">
              <tweet-media-left :show-line-down="true" :show-line-up="true"></tweet-media-left>
              <div class="media-content p-0 "><em class=" has-text-grey">{{ this.postedTweets.length - 2 }} more tweets&hellip;</em>
              </div>
            </div>
          </div>
        </div>
        <tweet v-if="this.postedTweets.length > 1" :show-line-up="true"
               :data="this.postedTweets[this.postedTweets.length - 1]" :show-media="false"></tweet>
      </div>

      <div v-else class="table-container">
        <table class="table is-fullwidth is-striped is-hoverable">
          <thead>
          <tr>
            <th></th>
            <th>Tweet</th>
            <th>Images</th>
            <th>Status</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(tweet, index) in $store.getters.tweets" :key="tweet.id">
            <td>{{ index + 1 }}</td>
            <td class="ellipsis-text tweet-summary">{{ tweet.text }}</td>
            <td>{{ tweet.image_ids.length }}</td>
            <td>
              <div v-if="postedTweets.length >= (index + 1)">
                <span class="icon has-text-success"><i class="fas fa-check-square"></i></span>
                <link-to-tweet :screen_name="$store.state.auth.profile.screen_name" :id="postedTweets[index].id">
                  <span class="icon"><i class="fab fa-twitter"></i></span>
                </link-to-tweet>
              </div>
              <span v-else-if="status === 'error' && errorTweetIndex === index" class="icon has-text-danger">
                      <i class="fas fa-exclamation-triangle"
                         title="An error occurred trying to post this tweet."></i></span>
              <span v-else-if="status === 'error'" class="icon has-text-warning">
                      <i class="fas fa-exclamation-triangle"
                         title="This tweet was not posted as an error occurred higher up the thread."></i>
                    </span>
              <span v-else-if="status === 'posting'" class="icon has-text-info">
                      <i class="fas fa-loading is-loading"></i></span>
              <span v-else></span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </section>


    <footer class="modal-card-foot">
      <template v-if="status === '' || status === 'posting'">
        <template v-if="scheduling">
          <b-button
              class="button is-primary"
              :class="{ 'is-loading': status === 'posting' }"
              icon-left="clock"
              @click="submitTweets">
            Schedule thread on @{{ $store.state.auth.profile && $store.state.auth.profile.screen_name }}
          </b-button>
        </template>
        <template v-else>
          <button
              class="button is-primary"
              :class="{ 'is-loading': status === 'posting' }"
              @click="submitTweets">
            <template v-if="scheduling">Schedule thread on</template>
            <template v-else>Post thread on</template>
            @{{ $store.state.auth.profile && $store.state.auth.profile.screen_name }}
          </button>
        </template>
      </template>
      <button v-if="status === ''" class="button" @click="$emit('close')" :disabled="status === 'posting'">Cancel
      </button>

      <div v-if="status === 'success'" class="buttons">
        <button class="button is-success" @click="$emit('close')" :disabled="status === 'posting'">
          Close
        </button>
        <button v-if="status === 'success' && !scheduling"
                class="button" @click="continueThread"
                :disabled="status === 'posting'">Extend thread with more tweets
        </button>
      </div>
      <button v-if="status === 'error'" class="button" @click="$emit('close')" :disabled="status === 'posting'">Close
        and Amend
      </button>


    </footer>
  </div>
</template>
<script>
import {addMinutes, format} from 'date-fns'

import LinkToTweet from "../controls/LinkToTweet"
import tweets from "../../api/tweets";
import Tweet from "../tweet/Tweet";
import TweetMediaLeft from "../TweetMediaLeft";
import {parseDate} from "chrono-node";

export default {
  name: 'submit-modal',
  components: {TweetMediaLeft, Tweet, LinkToTweet},

  props: ['scheduling', 'active'],
  data() {
    return {
      status: '',
      errorMsg: '',
      errorTweetIndex: undefined,
      postedTweets: [],
      scheduledFor: addMinutes(new Date(), 15),
      scheduledForPicker: addMinutes(new Date(), 15),
      scheduledForInput: ''
    }
  },

  created() {
    if (typeof window !== 'undefined') {
      document.addEventListener('keyup', this.keyUp)
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      document.removeEventListener('keyup', this.keyUp)
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.scheduling) {
        this.$refs.dateinput.focus()
      }
    })
  },

  watch: {
    scheduledForInput(val) {
      const parsedDateTime = parseDate(val, new Date(), {forwardDate: true})
      if (parsedDateTime !== null) {
        this.scheduledFor = parsedDateTime
        this.scheduledForPicker = parsedDateTime
        this.$nextTick(() => {
          this.$refs.dateinput.focus()
        })
      }
    },
    scheduledForPicker(val) {
      this.scheduledFor = val
    }
  },

  computed: {
    scheduledForDate() {
      if (this.scheduledFor) {
        return format(this.scheduledFor, "MMMM do, yyyy")
      } else {
        return ''
      }
    },
    scheduledForTime() {
      if (this.scheduledFor) {
        return format(this.scheduledFor, "k:mm")
      } else {
        return ''
      }
    },
    scheduleStatus() {
      if (this.scheduledFor) {
        if (this.scheduledFor <= new Date()) {
          return 'past'
        } else {
          return 'ready'
        }
      }
      return undefined
    }
  },

  methods: {
    keyUp(event) {
      // TODO bit of a hack - this prevents the ctrl-shift-enter of loading the modal
      // carrying through to activate here - not sure how else to prevent this happening.
      if (this.active && !event.ctrlKey && !event.altKey &&
          !event.shiftKey && !event.metaKey &&
          event.key === 'Enter') {
        if (this.status === '') {
          this.submitTweets()
        } else {
          // in all other cases - success or error - it's a close.
          this.$emit('close')
        }

      }
    },


    continueThread() {
      // on a successful post, this clears the thread down and sets the reply to the last tweet in the thread
      // allowing you to continue it naturally
      this.$store.dispatch('createNewThread')
      this.$store.commit('setReplyTo', this.postedTweets[this.postedTweets.length - 1])
      this.$emit('close')
    },

    submitTweets() {
      /* prevent posting if we have already tried */
      if (this.status !== '') return;

      /* send up all images and tweets to our server, which passes them on to twitter */
      this.status = 'posting';

      let postMethod = tweets.postThread
      let postData = {
        reply_to: this.$store.getters.replyTo ? this.$store.getters.replyTo.id : ''
      }
      if (this.scheduling) {
        postMethod = tweets.scheduleThread
        postData.scheduled_for = this.scheduledFor
      }

      postMethod(tweets.buildThreadPostFormData(postData)).then(response => {
        this.status = 'success'
        if (!this.scheduling) {
          this.postedTweets = response.data.posted_tweets
        } else {
          this.$emit('close')
          this.$buefy.snackbar.open({
            message: 'Thread scheduled',
            position: 'is-top',
            actionText: "OK",
            duration: 3000,
          })
        }
        this.$store.dispatch('removeActiveThread')

      }).catch(error => {
        this.status = 'error'
        if (error.response && error.response.data.twitter_error) {
          // this is an error proxied from twitter
          this.errorMsg = error.response.data.twitter_error
          this.postedTweets = error.response.data.posted_tweets
          this.errorTweetIndex = error.response.data.tweet_error_index
        } else {
          this.errorMsg = 'Submission or server error: ' + error
        }
      })
    }
  }
}
</script>
<style lang="scss">

.scheduling-info .message {
  border-radius: 0 !important;
}

.scheduling-info .message-body {
  border-radius: 0 !important;
}

.tweet-summary {
  width: 65%;
  max-width: 0;
}
</style>
